<template>
    <vx-expandable-card>
        <template v-slot:header>Documentation</template>
        <template v-slot:content>
            <div class="mb-4">
                The FTP Pull integration allows Voxie to automatically pull data from your SFTP servers and ingest it
                for processing.
            </div>
            <div class="mb-4">
                Each URL provided must point to either a single file or a directory on your SFTP server. If a directory
                is specified, Voxie will scan all files within that directory and import files that have been added or
                modified since the last pull. If a single file is specified, Voxie will only import that specific file
                if it has been modified since the last pull.
            </div>
            <div class="mb-4">
                All URLs are optional - configure only the data types you need to pull. Each data type can have its own
                SFTP URL, credentials, and SSH key.
            </div>
            <div class="mb-4">
                <strong>Data types:</strong>
            </div>
            <ul class="list-disc list-inside mb-4">
                <li><strong>Contacts:</strong> Contact information to import into Voxie.</li>
                <li><strong>Conversions:</strong> Conversion tracking data.</li>
                <li><strong>Groups:</strong> Group membership and organization data.</li>
            </ul>
            <div class="mb-4">
                <strong>URL format:</strong>
            </div>
            <ul class="list-disc list-inside mb-4">
                <li>Must start with <code>sftp://</code></li>
                <li>
                    Must end with a file extension (<code>.csv</code> or <code>.zip</code>) when pulling a single file.
                    For example: <code>sftp://example.com/path/to/file.csv</code> or
                    <code>sftp://example.com/path/to/data.zip</code>
                </li>
                <li>
                    Must end with a <code>/</code> when importing from a directory. For example:
                    <code>sftp://example.com/path/to/directory/</code>
                </li>
                <li>
                    May carry a query string after the file name or directory. For example:
                    <code>sftp://example.com/path/to/file.csv?ver=2</code>
                </li>
                <li>
                    Must NOT contain an <code>@</code> character — embedded credentials such as
                    <code>sftp://user:pass@host</code> are not allowed
                </li>
            </ul>
            <div class="mb-4">
                <strong>Authentication:</strong>
            </div>
            <ul class="list-disc list-inside mb-4">
                <li>Each URL requires either a password or an SSH private key for authentication</li>
                <li>Supported SSH key types: RSA, DSA, ECDSA, Ed25519</li>
                <li>Supported key formats: PKCS1, PKCS8, OpenSSH, PuTTY</li>
                <li>
                    <strong>Important:</strong> Passphrase-protected OpenSSH keys (<code
                        >-----BEGIN OPENSSH PRIVATE KEY-----</code
                    >) are <strong>not supported</strong> due to performance issues. Please use an unencrypted SSH key.
                </li>
            </ul>
            <div class="mb-4">
                <strong>File format support:</strong>
            </div>
            <ul class="list-disc list-inside mb-4">
                <li>Supported file formats: <code>.csv</code> (direct) and <code>.zip</code> (containing CSV files)</li>
                <li>
                    ZIP files are automatically extracted.
                    <strong>Only the first CSV file found in the ZIP is processed</strong> - ZIP files containing
                    multiple CSV files are not supported
                </li>
                <li>Maximum file size: 100MB (for both CSV and uncompressed size of ZIP contents)</li>
                <li>Files exceeding the size limit are skipped</li>
            </ul>
            <div class="mb-4">
                <strong>SFTP server requirements:</strong>
            </div>
            <ul class="list-disc list-inside mb-4">
                <li>
                    The SFTP user must have
                    <strong>both directory listing and file metadata (<code>stat</code>) permissions</strong> on the
                    configured path. If metadata cannot be retrieved for a file, that file will be skipped with a
                    warning.
                </li>
                <li>
                    Broken symlinks or symlinks pointing to inaccessible targets in the configured directory may cause
                    individual files to be skipped.
                </li>
                <li>
                    If files are consistently skipped, ask your SFTP server administrator to confirm that
                    <code>stat</code>/<code>lstat</code> SFTP subsystem commands are permitted for the user.
                </li>
            </ul>

            <hr class="my-6 border-gray-200" />

            <div class="mb-4">
                <strong>Contacts CSV format:</strong>
            </div>
            <div class="mb-4">
                Drop a <code>.csv</code> file into the configured SFTP path to create or update contacts. Rows are
                matched by <code>phone</code> and upserted on every import.
            </div>
            <div class="mb-4">
                <strong>Columns:</strong>
            </div>
            <ul class="list-disc list-inside mb-4">
                <li>
                    <code>phone</code> <strong>(required)</strong> — The contact's phone number. Serves as the unique
                    key for matching and upserting.
                </li>
                <li><code>first_name</code> — Contact's first name.</li>
                <li><code>last_name</code> — Contact's last name.</li>
                <li><code>email</code> — Contact's email address.</li>
                <li>
                    <code>group_id</code> — Associates the contact with a group/location. Must match an existing
                    <code>group_id</code> from your groups import.
                </li>
                <li>
                    <code>is_preferred</code> — Set to <code>false</code> to add the contact to the group without making
                    it their preferred group. When a phone is on several rows, set <code>true</code> on one and
                    <code>false</code> on the rest.
                </li>
                <li>
                    Any additional columns are stored as custom attributes on the contact and can be used for filtering
                    and segmentation.
                </li>
            </ul>
            <div class="mb-4">
                <strong>Rules:</strong>
            </div>
            <ul class="list-disc list-inside mb-4">
                <li>Header row is mandatory. Column names are case-sensitive.</li>
                <li>Encoding: UTF-8 (no BOM). Delimiter: comma.</li>
                <li>Leave optional cells blank — do not use <code>NULL</code> or <code>N/A</code>.</li>
                <li>File name format recommendation: <code>contact_import_YYYYMMDD.csv</code>.</li>
            </ul>
            <div class="mb-4">
                <strong>Example:</strong>
            </div>
            <csv-template :downloadHref="contactImportCsvUrl">
                <template v-slot:head>
                    <tr>
                        <th>phone</th>
                        <th>first_name</th>
                        <th>last_name</th>
                        <th>email</th>
                        <th>group_id</th>
                        <th>loyalty_tier</th>
                        <th>preferred_language</th>
                    </tr>
                </template>
                <tr>
                    <td>+12125551001</td>
                    <td>Ana</td>
                    <td>Reyes</td>
                    <td>ana.reyes@email.com</td>
                    <td>LOC-1001</td>
                    <td>Gold</td>
                    <td>en</td>
                </tr>
                <tr>
                    <td>+17205551003</td>
                    <td>Priya</td>
                    <td>Sharma</td>
                    <td></td>
                    <td>LOC-1003</td>
                    <td>Gold</td>
                    <td>es</td>
                </tr>
                <tr>
                    <td>+12125551005</td>
                    <td>Sofia</td>
                    <td>Alvarez</td>
                    <td>sofia.a@email.com</td>
                    <td></td>
                    <td></td>
                    <td>en</td>
                </tr>
            </csv-template>

            <hr class="my-6 border-gray-200" />

            <div class="mb-4">
                <strong>Conversions CSV format:</strong>
            </div>
            <div class="mb-4">
                Drop a <code>.csv</code> file into the configured SFTP path to ingest order/conversion data used for
                attribution reporting, ROI calculations, and campaign performance analysis.
            </div>
            <div class="mb-4">
                <strong>Columns:</strong>
            </div>
            <ul class="list-disc list-inside mb-4">
                <li>
                    <code>order_at</code> <strong>(required)</strong> — Timestamp of when the order occurred (e.g.
                    <code>2025-03-15T14:32:00-05:00</code>).
                </li>
                <li>
                    <code>customer_phone</code> <strong>(required)</strong> — The customer's phone number. Used to match
                    the conversion to an existing contact.
                </li>
                <li>
                    <code>order_value</code> <strong>(required)</strong> — Monetary value of the order (e.g.
                    <code>47.50</code>). Used for revenue attribution and ROI reporting.
                </li>
                <li>
                    <code>order_id</code> — Unique identifier for the order from your source system. Strongly
                    recommended — enables deduplication. If provided and already exists, the row is skipped.
                </li>
                <li>
                    <code>group_id</code> — Associates the conversion with a group/location. Must match an existing
                    <code>group_id</code> from your groups import.
                </li>
                <li>
                    Any additional columns are stored as custom attributes on the conversion. Contact-level custom
                    attributes are also supported and will be applied to the matched contact.
                </li>
            </ul>
            <div class="mb-4">
                <strong>Rules:</strong>
            </div>
            <ul class="list-disc list-inside mb-4">
                <li>Header row is mandatory. Column names are case-sensitive.</li>
                <li>Encoding: UTF-8 (no BOM). Delimiter: comma.</li>
                <li>Leave optional cells blank — do not use <code>NULL</code> or <code>N/A</code>.</li>
                <li>File name format recommendation: <code>conversion_import_YYYYMMDD.csv</code>.</li>
            </ul>
            <div class="mb-4">
                <strong>Example:</strong>
            </div>
            <csv-template :downloadHref="conversionImportCsvUrl">
                <template v-slot:head>
                    <tr>
                        <th>order_at</th>
                        <th>customer_phone</th>
                        <th>order_value</th>
                        <th>order_id</th>
                        <th>group_id</th>
                        <th>order_type</th>
                        <th>category</th>
                        <th>item_count</th>
                    </tr>
                </template>
                <tr>
                    <td>2025-03-15T14:32:00-05:00</td>
                    <td>+12125551001</td>
                    <td>47.50</td>
                    <td>ORD-90001</td>
                    <td>LOC-1001</td>
                    <td>dine-in</td>
                    <td>lunch</td>
                    <td>3</td>
                </tr>
                <tr>
                    <td>2025-03-17T11:20:00-05:00</td>
                    <td>+12125551005</td>
                    <td>31.25</td>
                    <td></td>
                    <td>LOC-1001</td>
                    <td>dine-in</td>
                    <td>lunch</td>
                    <td>2</td>
                </tr>
                <tr>
                    <td>2025-03-17T19:30:00-06:00</td>
                    <td>+13125551006</td>
                    <td>15.49</td>
                    <td>ORD-90006</td>
                    <td></td>
                    <td>delivery</td>
                    <td>dinner</td>
                    <td>1</td>
                </tr>
            </csv-template>

            <hr class="my-6 border-gray-200" />

            <div class="mb-4">
                <strong>Groups CSV format:</strong>
            </div>
            <div class="mb-4">
                Drop a <code>.csv</code> file into the configured SFTP path to create or update groups
                (locations/franchises). Rows are matched by <code>group_id</code> and upserted on every import.
            </div>
            <div class="mb-4">
                <strong>Columns:</strong>
            </div>
            <ul class="list-disc list-inside mb-4">
                <li>
                    <code>group_id</code> <strong>(required)</strong> — Unique stable identifier used to match and
                    upsert (e.g. <code>LOC-1001</code>, <code>store_42</code>).
                </li>
                <li>
                    <code>group_name</code> <strong>(required)</strong> — Human-readable name for the group/location.
                    Updated on every import if changed.
                </li>
                <li>
                    <code>org_unit_id</code> — Numeric ID of the organizational unit to assign this group to. Leave
                    blank to leave the current assignment unchanged, or set to <code>0</code> to remove the group from
                    its organizational unit.
                </li>
                <li>
                    <code>group_time_zone</code> — IANA time zone string (e.g. <code>America/New_York</code>). Controls
                    message scheduling and reporting. Defaults to <code>America/New_York</code> if omitted.
                </li>
                <li><code>group_address</code> — Street address line 1.</li>
                <li><code>group_address_line_2</code> — Street address line 2 (suite, unit, etc.).</li>
                <li><code>group_city</code> — City.</li>
                <li><code>group_state</code> — State or province code (e.g. <code>NY</code>, <code>CA</code>).</li>
                <li><code>group_zip</code> — ZIP or postal code.</li>
                <li>
                    <code>group_country</code> — ISO 3166-1 alpha-2 country code (e.g. <code>US</code>,
                    <code>CA</code>). Required when <code>group_state</code> or <code>group_zip</code> is provided.
                </li>
                <li>
                    Any additional columns are stored as custom attributes on the group and can be used for filtering
                    and segmentation.
                </li>
            </ul>
            <div class="mb-4">
                <strong>Rules:</strong>
            </div>
            <ul class="list-disc list-inside mb-4">
                <li>Header row is mandatory. Column names are case-sensitive.</li>
                <li>Encoding: UTF-8 (no BOM). Delimiter: comma.</li>
                <li>Leave optional cells blank — do not use <code>NULL</code> or <code>N/A</code>.</li>
                <li>File name format recommendation: <code>group_import_YYYYMMDD.csv</code>.</li>
            </ul>
            <div class="mb-4">
                <strong>Example:</strong>
            </div>
            <csv-template :downloadHref="groupImportCsvUrl">
                <template v-slot:head>
                    <tr>
                        <th>group_id</th>
                        <th>group_name</th>
                        <th>org_unit_id</th>
                        <th>group_time_zone</th>
                        <th>group_address</th>
                        <th>group_address_line_2</th>
                        <th>group_city</th>
                        <th>group_state</th>
                        <th>group_zip</th>
                        <th>group_country</th>
                        <th>region</th>
                        <th>district_manager</th>
                    </tr>
                </template>
                <tr>
                    <td>LOC-1001</td>
                    <td>Downtown Flagship</td>
                    <td>1</td>
                    <td>America/New_York</td>
                    <td>123 Main St</td>
                    <td>Suite 100</td>
                    <td>New York</td>
                    <td>NY</td>
                    <td>10001</td>
                    <td>US</td>
                    <td>Northeast</td>
                    <td>Maria Santos</td>
                </tr>
                <tr>
                    <td>LOC-1002</td>
                    <td>Westside Mall</td>
                    <td></td>
                    <td>America/Chicago</td>
                    <td>456 West Ave</td>
                    <td></td>
                    <td>Chicago</td>
                    <td>IL</td>
                    <td>60601</td>
                    <td>US</td>
                    <td>Midwest</td>
                    <td>James Chen</td>
                </tr>
                <tr>
                    <td>LOC-1006</td>
                    <td>Lakefront Plaza</td>
                    <td>0</td>
                    <td>America/Chicago</td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td>Midwest</td>
                    <td>James Chen</td>
                </tr>
            </csv-template>
        </template>
    </vx-expandable-card>
</template>
<script setup lang="ts">
import { VxExpandableCard } from '@voxie/frontend-components';
import CsvTemplate from '~/components/general/CsvTemplate.vue';
import contactImportCsvUrl from '@csv/sftp_contact_import_example.csv';
import conversionImportCsvUrl from '@csv/sftp_conversion_import_example.csv';
import groupImportCsvUrl from '@csv/sftp_group_import_example.csv';
</script>
